// GPU-side timing via EXT_disjoint_timer_query_webgl2.
//
// performance.now() around WebGL calls only measures CPU command-submission;
// the actual GPU work is queued and runs asynchronously. To attribute time
// to vertex/fragment shaders we need timer queries. Results land 1-3 frames
// late, so each scope keeps a ring of in-flight queries and we read the
// oldest available one. If the extension is missing (some headless setups,
// older drivers) the timer becomes a no-op reporting 0 ms.
//
// Frame vs. per-pass mode: TIME_ELAPSED queries cannot nest, so we can't
// have a frame-wide query running concurrently with per-pass ones. To get
// a trustworthy whole-frame GPU number (especially on macOS ANGLE/Metal
// where per-pass times can be inflated/aggregated), the timer alternates
// between modes per frame:
//   * per-pass frame: all begin/end calls run as normal; 'frame' is silent.
//   * frame-only frame: per-pass begin/end no-op; one outer query brackets
//     from beginFrame() to endFrame().
// Both sets of scopes retain their last settled value, so readers always
// see a recent number (worst case 1 frame stale). Mode is configurable
// via setMode() for users who want all samples in one mode.

const RING_SIZE = 4;
const FRAME_SCOPE = 'frame';

type Ext = {
  TIME_ELAPSED_EXT: number;
  GPU_DISJOINT_EXT: number;
};

type Scope = {
  queries: (WebGLQuery | null)[];
  inFlight: boolean[];
  writeIndex: number;
  lastResultMs: number;
};

export type GpuTimingMode = 'alternate' | 'per-pass' | 'frame';

export class GpuTimer {
  private gl: WebGL2RenderingContext;
  private ext: Ext | null;
  private scopes = new Map<string, Scope>();
  private activeScope: string | null = null;
  private mode: GpuTimingMode = 'alternate';
  private alternateCounter = 0;
  // True when the current frame is in per-pass mode (begin/end run). False
  // when it's a frame-only frame (begin/end no-op, 'frame' wraps everything).
  private framePerPassActive = true;
  private rendererStringCache: string | null = null;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    const ext = gl.getExtension('EXT_disjoint_timer_query_webgl2') as Ext | null;
    this.ext = ext;
    if (!ext) {
      console.info('[GpuTimer] EXT_disjoint_timer_query_webgl2 unavailable; GPU times will be 0.');
    }
  }

  public get available(): boolean {
    return this.ext !== null;
  }

  public setMode(mode: GpuTimingMode): void {
    this.mode = mode;
  }

  public getMode(): GpuTimingMode {
    return this.mode;
  }

  // Unmasked GPU renderer string (e.g. "ANGLE (Apple, Apple M2, OpenGL 4.1)").
  // Used by the perf overlay to flag platforms where per-pass times are
  // known unreliable. Cached because the extension is one-shot in some
  // browsers and the string never changes per context.
  public getRendererString(): string {
    if (this.rendererStringCache !== null) return this.rendererStringCache;
    const dbg = this.gl.getExtension('WEBGL_debug_renderer_info') as
      | { UNMASKED_RENDERER_WEBGL: number }
      | null;
    this.rendererStringCache = dbg
      ? ((this.gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) as string) ?? '')
      : '';
    return this.rendererStringCache;
  }

  // True for macOS Chrome on Metal — TIME_ELAPSED queries here often return
  // per-pass values close to whole-flush elapsed time, making the per-pass
  // sum massively exceed wall-clock. Use the frame query as the trust signal.
  public get perPassUnreliable(): boolean {
    const s = this.getRendererString();
    return /Metal/i.test(s) || /Apple/i.test(s);
  }

  // Call once at the top of the frame, before any begin(). When the GPU
  // timeline crosses a disjoint event (mode switch, etc.) all in-flight
  // measurements are unreliable and must be discarded per the spec.
  public beginFrame(): void {
    if (!this.ext) return;
    const disjoint = this.gl.getParameter(this.ext.GPU_DISJOINT_EXT) as boolean;
    if (disjoint) {
      for (const scope of this.scopes.values()) {
        for (let i = 0; i < RING_SIZE; i++) scope.inFlight[i] = false;
      }
    }
    // Decide this frame's mode before any begin() call fires.
    if (this.mode === 'alternate') {
      this.framePerPassActive = (this.alternateCounter++ & 1) === 0;
    } else {
      this.framePerPassActive = this.mode === 'per-pass';
    }
    if (!this.framePerPassActive) {
      this.beginInternal(FRAME_SCOPE);
    }
  }

  // Call once at the very end of the frame, after the last GPU command. Closes
  // out the frame-wide query on frame-only frames; no-op on per-pass frames.
  public endFrame(): void {
    if (!this.ext) return;
    if (!this.framePerPassActive && this.activeScope === FRAME_SCOPE) {
      this.endInternal();
    }
  }

  public begin(name: string): void {
    if (!this.ext || !this.framePerPassActive) return;
    this.beginInternal(name);
  }

  public end(): void {
    if (!this.ext || !this.framePerPassActive) return;
    this.endInternal();
  }

  private beginInternal(name: string): void {
    if (this.activeScope !== null) {
      throw new Error(
        `[GpuTimer] cannot begin '${name}' while '${this.activeScope}' is active (TIME_ELAPSED queries cannot nest)`,
      );
    }
    const scope = this.getOrCreateScope(name);
    const slot = scope.writeIndex;
    let q = scope.queries[slot];
    if (!q) {
      q = this.gl.createQuery();
      scope.queries[slot] = q;
    }
    if (scope.inFlight[slot]) {
      // Slot still busy from a prior frame — drop oldest measurement to
      // recycle. With RING_SIZE=4 this only happens if results stall (rare).
      scope.inFlight[slot] = false;
    }
    this.gl.beginQuery(this.ext!.TIME_ELAPSED_EXT, q!);
    scope.inFlight[slot] = true;
    this.activeScope = name;
  }

  private endInternal(): void {
    if (this.activeScope === null) return;
    const scope = this.scopes.get(this.activeScope)!;
    this.gl.endQuery(this.ext!.TIME_ELAPSED_EXT);
    scope.writeIndex = (scope.writeIndex + 1) % RING_SIZE;
    this.activeScope = null;
  }

  // Polls the ring for any settled result and updates lastResultMs. Call
  // once per frame per scope (or just call getLastResultMs which polls).
  public poll(name: string): number {
    const scope = this.scopes.get(name);
    if (!scope || !this.ext) return 0;
    const gl = this.gl;
    // Walk ring oldest-first relative to writeIndex; settle whatever is
    // ready and keep the most recent settled value.
    for (let i = 0; i < RING_SIZE; i++) {
      const slot = (scope.writeIndex + i) % RING_SIZE;
      if (!scope.inFlight[slot]) continue;
      const q = scope.queries[slot]!;
      const ready = gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE) as boolean;
      if (!ready) continue;
      const ns = gl.getQueryParameter(q, gl.QUERY_RESULT) as number;
      scope.lastResultMs = ns / 1_000_000;
      scope.inFlight[slot] = false;
    }
    return scope.lastResultMs;
  }

  public getLastResultMs(name: string): number {
    return this.poll(name);
  }

  public destroy(): void {
    for (const scope of this.scopes.values()) {
      for (const q of scope.queries) {
        if (q) this.gl.deleteQuery(q);
      }
    }
    this.scopes.clear();
    this.activeScope = null;
  }

  private getOrCreateScope(name: string): Scope {
    let scope = this.scopes.get(name);
    if (!scope) {
      scope = {
        queries: new Array(RING_SIZE).fill(null),
        inFlight: new Array(RING_SIZE).fill(false),
        writeIndex: 0,
        lastResultMs: 0,
      };
      this.scopes.set(name, scope);
    }
    return scope;
  }
}
